import type { BestRun } from '../game/session';
import type { Archetype, Mode, Tier } from '../game/types';

export interface ShareContext {
  mode: Mode;
  /** Daily grid number; ignored in practice mode. */
  day: number | null;
  /** The player is on today's grid (not the archive) — streaks only apply there. */
  isToday: boolean;
  best: BestRun;
  firstMW: number | null;
  par: number;
  tier: Tier;
  beatReference: boolean;
  /** The best layout matched the reference. */
  matched: boolean;
  /** The day's shape, glyphed on the first line. */
  archetype: Archetype;
  /** The cold read and every new best after it, oldest first. */
  trail: number[];
  /** Name of the reference's topology, shown only on a perfect (GDD §3.5). */
  topology: string | null;
  /** Reached the perfect grid having never removed a tower. */
  flawless: boolean;
  /** The reference was studied before this grid was perfected. */
  studied: boolean;
  streak: number;
}

/**
 * The day's shape, as the share names it.
 *
 * Emoji live here and nowhere else in the product (STYLEGUIDE §6): the share
 * renders in someone else's chat, not in this UI. The glyph says what
 * everyone was up against, which is not a spoiler — the map is shared, the
 * route is the secret.
 */
const ARCHETYPE_GLYPH: Record<Archetype, string> = {
  river: '🌊',
  delta: '🏞',
  canyon: '⛰',
  archipelago: '🏝',
  radial: '🧭',
};

/** Most milestones the MW line carries, however long the session ran. */
const MAX_MILESTONES = 5;

/**
 * Thin a run of readings to `n`, always keeping the first and the last.
 *
 * The endpoints are the ones that carry meaning — where the player started
 * cold and where they finished — so the thinning takes from the middle.
 */
export function thin(trail: number[], n = MAX_MILESTONES): number[] {
  if (trail.length <= n) return trail;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    out.push(trail[Math.round((i * (trail.length - 1)) / (n - 1))]);
  }
  return out;
}

/**
 * The brag. Spoiler-free by design — it reports how the grid was fought and
 * how well it was tuned, never where the towers went.
 */
export function buildShareText(ctx: ShareContext): string {
  const { best, firstMW, tier, trail } = ctx;

  // `84→96` compressed a fight to its endpoints; the milestones show the
  // fight. A run the game has no milestones for keeps the endpoints it does
  // have rather than inventing the middle (GDD Pillar 4).
  const target = ctx.matched ? ' 🎯' : '';
  const mw =
    firstMW != null && firstMW === best.mw
      ? `⚡ ${best.mw} MW · first take`
      : trail.length >= 2
        ? `⚡ ${thin(trail).join(' · ')}${target} MW`
        : firstMW != null
          ? `⚡ ${firstMW} · ${best.mw}${target} MW`
          : `⚡ ${best.mw}${target} MW`;

  const rank =
    `${tier.emoji} ${tier.name}` +
    (ctx.topology ? ` · ${ctx.topology}` : '') +
    (ctx.beatReference ? ' · beat the best known grid!' : '');

  // No emoji for the towers: Unicode has nothing for a transmission tower (🗼
  // is Tokyo Tower), and the share is plain text, so it can't use the drawn
  // glyph the page uses. A word is more honest than the wrong picture.
  const lines = [
    `Wirespan ${ctx.mode === 'daily' ? '#' + ctx.day : 'practice'} ${ARCHETYPE_GLYPH[ctx.archetype]}`,
    mw,
    rank,
  ];

  const marks = [ctx.flawless ? '✨ flawless' : '', ctx.studied ? '📖 studied' : ''].filter(
    Boolean,
  );
  if (marks.length) lines.push(marks.join(' · '));

  if (ctx.mode === 'daily' && ctx.isToday && ctx.streak > 1) {
    lines.push(`🔥 ${ctx.streak}-day streak`);
  }
  return lines.join('\n');
}

/** Clipboard API where available, with the old textarea trick as a fallback. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    /* blocked or unavailable — try the legacy path */
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    ta.remove();
  }
}
